/**
 * TrueLayer Payments v3 authorization flow: after POST .../authorization-flow,
 * the next step may be provider_selection or scheme_selection before redirect.
 */

/** Set before opening the bank so /truelayer-connect can recognise a payment return (shared across tabs). */
export const TRUELAYER_PENDING_PAYMENT_ID_KEY = 'truelayer_pending_payment_id';

/** Call immediately before opening the bank/OAuth URL so the return tab can treat /truelayer-connect as a payment completion. */
export function rememberPendingTrueLayerPaymentId(paymentId: string): void {
  try {
    localStorage.setItem(TRUELAYER_PENDING_PAYMENT_ID_KEY, paymentId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function getTrueLayerAuthorizationNext(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const af = d.authorization_flow as Record<string, unknown> | undefined;
  const fromAf = (af?.actions as Record<string, unknown> | undefined)?.next;
  if (fromAf && typeof fromAf === 'object') return fromAf as Record<string, unknown>;
  const legacy = (d.actions as Record<string, unknown> | undefined)?.next;
  if (legacy && typeof legacy === 'object') return legacy as Record<string, unknown>;
  return null;
}

export function getTrueLayerRedirectUri(next: Record<string, unknown> | null): string | null {
  if (next?.type === 'redirect' && next.uri) return String(next.uri);
  return null;
}

type PostFn = (path: string, body: Record<string, unknown>) => Promise<{
  success: boolean;
  data?: unknown;
  error?: string;
}>;

export type TrueLayerAuthLoopResult =
  | { status: 'redirect_opened' }
  | { status: 'error'; message: string };

export type TrueLayerAuthLoopOptions = {
  /** Beneficiary IBAN — used to pick a mock/live provider whose country_code matches the IBAN country */
  beneficiaryIban?: string | null;
};

/** ISO 3166-1 alpha-2 from IBAN (first two letters after stripping spaces). */
export function extractIbanCountryCode(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  if (compact.length < 4) return null;
  const cc = compact.slice(0, 2);
  if (!/^[A-Z]{2}$/.test(cc)) return null;
  return cc;
}

type ProviderRow = { id?: string; country_code?: string };

/**
 * Prefer a provider whose country_code matches the IBAN country; otherwise first in list.
 */
export function pickProviderIdForIbanCountry(providers: unknown[], ibanCountry: string | null): string | null {
  if (!Array.isArray(providers) || providers.length === 0) return null;
  const rows = providers as ProviderRow[];
  if (ibanCountry) {
    const upper = ibanCountry.toUpperCase();
    const match = rows.find((p) => p?.id && String(p.country_code || '').toUpperCase() === upper);
    if (match?.id) return String(match.id);
  }
  const first = rows[0];
  return first?.id ? String(first.id) : null;
}

/**
 * Walks provider_selection → scheme_selection → … until redirect, then opens bank URL.
 */
export async function runTrueLayerPaymentAuthorizationLoop(
  post: PostFn,
  paymentId: string,
  resourceToken: string,
  initialFlowData: unknown,
  onStatus: (message: string) => void,
  onRedirectOpening?: (redirectUri: string) => void,
  options?: TrueLayerAuthLoopOptions
): Promise<TrueLayerAuthLoopResult> {
  let flowData: unknown = initialFlowData;
  const maxSteps = 8;

  for (let step = 0; step < maxSteps; step++) {
    const next = getTrueLayerAuthorizationNext(flowData);
    if (!next) {
      return { status: 'error', message: 'Authorization flow returned no next step.' };
    }

    const redirectUri = getTrueLayerRedirectUri(next);
    if (redirectUri) {
      onStatus('Redirecting to your bank to authorize payment...');
      rememberPendingTrueLayerPaymentId(paymentId);
      onRedirectOpening?.(redirectUri);
      window.open(redirectUri, '_blank', 'noopener,noreferrer');
      return { status: 'redirect_opened' };
    }

    const nextType = String(next.type || '');

    if (nextType === 'provider_selection') {
      const providers = Array.isArray(next.providers) ? next.providers : [];
      const ibanCountry = extractIbanCountryCode(options?.beneficiaryIban ?? '');
      const providerId = pickProviderIdForIbanCountry(providers, ibanCountry);
      if (!providerId) {
        return { status: 'error', message: 'No bank providers available. Try again or contact support.' };
      }
      if (ibanCountry && providers.length > 0) {
        const matched = (providers as ProviderRow[]).some(
          (p) => p?.id === providerId && String(p.country_code || '').toUpperCase() === ibanCountry
        );
        onStatus(
          matched
            ? `Choosing bank for ${ibanCountry}…`
            : `No bank listed for ${ibanCountry}; using fallback provider…`
        );
      } else {
        onStatus('Choosing bank provider…');
      }
      const sub = await post(
        `/api/banking/truelayer/payments/${paymentId}/authorization-flow/actions/provider-selection`,
        { resource_token: resourceToken, provider_id: providerId }
      );
      if (!sub.success) {
        return { status: 'error', message: sub.error || 'Failed to submit bank provider.' };
      }
      flowData = sub.data;
      continue;
    }

    if (nextType === 'scheme_selection') {
      const schemes = Array.isArray(next.schemes) ? next.schemes : [];
      const pick =
        (schemes.find((s: unknown) => (s as { recommended?: boolean }).recommended) as
          | { id?: string }
          | undefined) || (schemes[0] as { id?: string } | undefined);
      const schemeId = pick?.id;
      if (!schemeId) {
        return { status: 'error', message: 'No payment scheme available for this bank.' };
      }
      onStatus('Selecting payment scheme...');
      const sub = await post(
        `/api/banking/truelayer/payments/${paymentId}/authorization-flow/actions/scheme-selection`,
        { resource_token: resourceToken, scheme_id: schemeId }
      );
      if (!sub.success) {
        return { status: 'error', message: sub.error || 'Failed to submit payment scheme.' };
      }
      flowData = sub.data;
      continue;
    }

    return {
      status: 'error',
      message: `Unsupported authorization step: ${nextType || 'unknown'}.`,
    };
  }

  return { status: 'error', message: 'Authorization flow did not reach a bank redirect in time.' };
}
